/**
 * Safari fallback for Add to cart.
 *
 * This theme relies on customized built-in elements:
 * - <form is="product-form"> (extends HTMLFormElement)
 * - <button is="custom-button"> (extends HTMLButtonElement)
 *
 * iOS Safari does not support customized built-in elements, so the theme's JS
 * doesn't intercept the form submission, causing a full page navigation to /cart.
 *
 * We emulate the theme's ProductForm submit handler (AJAX add + cart change events)
 * to keep the cart drawer behavior consistent with Chrome.
 */
(() => {
  const submittingState = new WeakMap();

  const isCustomizedBuiltInUpgraded = (element, customElementName) => {
    const CustomElementClass = window.customElements?.get?.(customElementName);
    return Boolean(CustomElementClass && element instanceof CustomElementClass);
  };

  const ensureButtonLoaderMarkup = (button) => {
    if (!(button instanceof HTMLButtonElement)) return;
    if (button.querySelector(".button__loader")) return;

    const contentWrapper = document.createElement("div");
    while (button.firstChild) contentWrapper.appendChild(button.firstChild);

    const loader = document.createElement("span");
    loader.className = "button__loader";
    loader.innerHTML = "<span></span><span></span><span></span>";

    contentWrapper.style.transform = "translateY(0px)";
    contentWrapper.style.opacity = "1";
    loader.style.transform = "translateY(-10px)";
    loader.style.opacity = "0";

    button.appendChild(contentWrapper);
    button.appendChild(loader);
  };

  const setButtonBusy = (button, busy) => {
    if (!(button instanceof HTMLButtonElement)) return;

    button.toggleAttribute("aria-busy", busy);

    ensureButtonLoaderMarkup(button);

    const contentElement = button.firstElementChild;
    const loaderElement = button.querySelector(".button__loader");

    if (!(contentElement instanceof HTMLElement) || !(loaderElement instanceof HTMLElement)) return;

    if (busy) {
      contentElement.style.opacity = "0";
      contentElement.style.transform = "translateY(0px)";
      loaderElement.style.opacity = "1";
      loaderElement.style.transform = "translateY(0px)";
    } else {
      contentElement.style.opacity = "1";
      contentElement.style.transform = "translateY(0px)";
      loaderElement.style.opacity = "0";
      loaderElement.style.transform = "translateY(-10px)";
    }
  };

  const shouldHandleProductFormSubmit = (form) => {
    if (!(form instanceof HTMLFormElement)) return false;
    if (form.getAttribute("is") !== "product-form") return false;

    // If the browser supports customized built-in elements and the form is upgraded,
    // let the theme's ProductForm class handle it.
    if (isCustomizedBuiltInUpgraded(form, "product-form")) return false;

    return true;
  };

  const getVariantIdFromFormData = (formData) => {
    const variantId = formData.get("id");
    return typeof variantId === "string" && variantId.trim() !== "" ? variantId : null;
  };

  const onSubmitCapture = async (event) => {
    const form = event.target;
    if (!shouldHandleProductFormSubmit(form)) return;

    event.preventDefault();

    const shopRoot = window.Shopify?.routes?.root || "/";

    if (submittingState.get(form) === true) return;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    submittingState.set(form, true);

    const submitButtons = Array.from(form.elements).filter(
      (el) => el instanceof HTMLButtonElement && el.type === "submit",
    );
    submitButtons.forEach((btn) => setButtonBusy(btn, true));

    try {
      const themeSettings = window.themeVariables?.settings || {};

      let sectionsToBundle = ["variant-added"];
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:prepare-bundled-sections", {
          bubbles: true,
          detail: { sections: sectionsToBundle },
        }),
      );

      const formData = new FormData(form);
      formData.set("sections", sectionsToBundle.join(","));

      const variantId = getVariantIdFromFormData(formData);
      if (variantId) {
        formData.set("sections_url", `${shopRoot}variants/${variantId}`);
      }

      const response = await fetch(`${shopRoot}cart/add.js`, {
        body: formData,
        method: "POST",
        headers: { "X-Requested-With": "XMLHttpRequest" },
      });

      const responseJson = await response.json();

      if (response.ok) {
        if (themeSettings.cartType === "page" || themeSettings.pageType === "cart") {
          window.location.href = `${shopRoot}cart`;
          return;
        }

        const cartContent = await (await fetch(`${shopRoot}cart.js`)).json();
        cartContent.sections = responseJson.sections;

        form.dispatchEvent(
          new CustomEvent("variant:add", {
            bubbles: true,
            detail: {
              items: responseJson.hasOwnProperty("items") ? responseJson.items : [responseJson],
              cart: cartContent,
            },
          }),
        );

        document.documentElement.dispatchEvent(
          new CustomEvent("cart:change", {
            bubbles: true,
            detail: { baseEvent: "variant:add", cart: cartContent },
          }),
        );
      } else {
        form.dispatchEvent(
          new CustomEvent("cart:error", {
            bubbles: true,
            detail: { error: responseJson.description },
          }),
        );
        document.dispatchEvent(new CustomEvent("cart:refresh"));
      }
    } catch (error) {
      form.dispatchEvent(
        new CustomEvent("cart:error", {
          bubbles: true,
          detail: { error: error instanceof Error ? error.message : "Add to cart failed" },
        }),
      );
      document.dispatchEvent(new CustomEvent("cart:refresh"));
    } finally {
      submitButtons.forEach((btn) => setButtonBusy(btn, false));
      submittingState.set(form, false);
    }
  };

  document.addEventListener("submit", onSubmitCapture, true);
})();
